import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    styled,
    Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { systemDateFormat } from "../../../../constants";
import {
    IWithClassName,
    IWithThemeProps,
} from "../../../../theme/default.theme";
import { minDate } from "../../const/historical-options";
import { FormParams } from "../../enums/form-params";
import { Steps } from "../../enums/steps";
import { TolerancePeriod, ToleranceRange } from "../../enums/tolerance";
import { IError } from "../../interface/form";
import DialogBox from "./dialog-box";
import SnapShotFrequency from "./snapshot-frequency";
import ToleranceContainer from "./tolerance";

interface IProps extends IWithClassName {
    show: boolean;
    data: Array<string>;
    onSelectChange: (
        step: Steps,
        label: FormParams,
    ) => (options: Array<string> | string | number | boolean) => void;
    snapshotDate?: string;
    tolerance: number;
    toleranceRange: ToleranceRange;
    tolerancePeriod: TolerancePeriod;
    setFrequency: boolean;
    error: IError;
}

const HistoricalOption = ({
    show,
    className,
    data,
    onSelectChange,
    snapshotDate,
    tolerance,
    tolerancePeriod,
    toleranceRange,
    setFrequency,
    error,
}: IProps) => {
    const [duplicateRow, setDuplicateRow] = useState<string | undefined>();
    const [locationAsOf, setLocationAsOf] = useState<Dayjs | null>(dayjs());
    const [calendarOpen, setCalendarOpen] = useState(false);

    useEffect(() => {
        if (snapshotDate) {
            setLocationAsOf(
                dayjs(snapshotDate).isValid() ? dayjs(snapshotDate) : dayjs(),
            );
        }
    }, [snapshotDate]);

    const onSelectSnapshotDate = (date: Dayjs | null, open = false) => {
        setLocationAsOf(date);
        setCalendarOpen(open);

        onSelectChange(
            Steps.LIST_DOWNLOAD_OPTIONS,
            FormParams.SNAPSHOT_DATE,
        )(show && date ? date.format(systemDateFormat) : "");
    };

    const handleMonthChange = (newMonth: Dayjs) => {
        const currentDate = locationAsOf;
        if (currentDate) {
            const isNextMonth = newMonth.isAfter(currentDate, "month");
            const adjustedDate = isNextMonth
                ? currentDate.add(1, "month")
                : currentDate.subtract(1, "month");

            onSelectSnapshotDate(adjustedDate, true);
        }
    };

    return (
        <>
            {show && (
                <Grid className={className}>
                    <DialogBox
                        open={!!duplicateRow}
                        date={duplicateRow ?? ""}
                        handleClose={() => setDuplicateRow(undefined)}
                    />
                    <Grid item>
                        <FormControl className="content-inline">
                            <FormLabel className="label-center">
                                Locations as of:
                            </FormLabel>
                            <Grid>
                                <Grid item>
                                    <DatePicker
                                        minDate={minDate}
                                        maxDate={dayjs()}
                                        open={calendarOpen}
                                        value={locationAsOf}
                                        slotProps={{
                                            actionBar: {
                                                actions: ["clear"],
                                            },
                                            textField: {
                                                size: "small",
                                                variant: "standard",
                                                onClick: () =>
                                                    setCalendarOpen(true),
                                            },
                                        }}
                                        onClose={() => setCalendarOpen(false)}
                                        onChange={(newDate) =>
                                            onSelectSnapshotDate(newDate)
                                        }
                                        onMonthChange={(newMonth) =>
                                            handleMonthChange(newMonth)
                                        }
                                    />
                                </Grid>
                                <Grid item>
                                    {error.snapshotDate && (
                                        <FormHelperText error>
                                            {error.snapshotDate}
                                        </FormHelperText>
                                    )}
                                </Grid>
                            </Grid>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <Tooltip title="Quickly Add Multiple Snapshots to your List Builder">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        onChange={({ currentTarget }) =>
                                            onSelectChange(
                                                Steps.LIST_DOWNLOAD_OPTIONS,
                                                FormParams.SET_FREQUENCY,
                                            )(currentTarget.checked)
                                        }
                                        checked={setFrequency}
                                    />
                                }
                                label="Add Additional Dates"
                            />
                        </Tooltip>
                    </Grid>
                    {setFrequency && (
                        <SnapShotFrequency
                            data={data}
                            onSelectChange={onSelectChange}
                            setDuplicateRow={setDuplicateRow}
                        />
                    )}
                    <ToleranceContainer
                        tolerance={tolerance}
                        tolerancePeriod={tolerancePeriod}
                        toleranceRange={toleranceRange}
                        onSelectChange={onSelectChange}
                    />
                </Grid>
            )}
        </>
    );
};

export default styled(HistoricalOption)(({ theme }: IWithThemeProps) => ({
    marginBottom: theme?.spacing(1),
    gap: 10,
    display: "grid",
    ".content-inline": {
        display: "flex",
        flexDirection: "row",
        gap: 5,
    },
    ".label-center": {
        margin: "auto 0",
    },
    ".MuiInputAdornment-root": {
        display: "none",
    },
}));
